<template>
    <div :class="`title-algin-${titleAlign} title`">
        <h3 :class="`h1-size-${titleSize}`">{{ titleText }}</h3>
        <h5 :class="`h3-size-${titleSize}`" v-if="subTitle">{{ subTitle }}</h5>
    </div>
</template>

<script>
export default {
    name: "FormTitle",
    props: {
        titleText: {
            type: String,
            required: true,
        },
        subTitle: {
            type: String,
            required: true,
        },
        titleSize: {
            type: String,
            default: "24px",
        },
        titleAlign: {
            type: String,
            default: "top",
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        }
    },
    mounted() {
        this.emitValues();
    },
    watch: {
        titleText: "emitValues",
        subTitle: "emitValues",
    },
    methods: {
        emitValues() {
            this.$emit("input", {
                titleText: this.titleText,
                subTitle: this.subTitle,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
// Alignment Classes
.title-algin-center {
    text-align: center !important;
}

.title-algin-right {
    text-align: right !important;
}

.title-algin-left {
    text-align: left !important;
}

// Size Classes for Main Title (h3)
.h1-size-wide {
    font-size: clamp(1.75rem, 4vw, 2.25rem) !important;
    font-weight: 700 !important;
    letter-spacing: -0.025em !important;
}

.h1-size-small {
    font-size: clamp(1.25rem, 3vw, 1.5rem) !important;
    font-weight: 600 !important;
    letter-spacing: -0.015em !important;
}

.h1-size-24px {
    font-size: clamp(1.5rem, 3.5vw, 1.75rem) !important;
    font-weight: 650 !important;
    letter-spacing: -0.02em !important;
}

// Size Classes for Subtitle (h5)
.h3-size-wide {
    font-size: clamp(1.125rem, 2.5vw, 1.25rem) !important;
    font-weight: 500 !important;
    letter-spacing: 0.01em !important;
}

.h3-size-small {
    font-size: clamp(0.9rem, 2vw, 1rem) !important;
    font-weight: 450 !important;
    letter-spacing: 0.005em !important;
}

.h3-size-24px {
    font-size: clamp(1rem, 2.25vw, 1.125rem) !important;
    font-weight: 475 !important;
    letter-spacing: 0.008em !important;
}

.title {
    padding: clamp(1rem, 2.5vw, 1.5rem) clamp(0.75rem, 2vw, 1.25rem);
    width: 100%;
    max-width: 700px;
    margin: clamp(0.75rem, 2vw, 1.25rem) auto;
    text-align: left;
    position: relative;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    // Subtle background with glassmorphism
    background: linear-gradient(145deg, 
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    
    // Subtle border
    border: 1px solid rgba(255, 255, 255, 0.08);
    
    // Hover effect
    &:hover {
        background: linear-gradient(145deg, 
            rgba(255, 255, 255, 0.04) 0%,
            rgba(255, 255, 255, 0.02) 100%);
        transform: translateY(-1px);
    }

    h3 {
        margin-bottom: clamp(0.375rem, 1vw, 0.5rem);
        color: var(--text-primary, rgba(55, 65, 81, 0.95));
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        line-height: 1.3;
        transition: all 0.3s ease;
        position: relative;
        
        // Animation on load
        animation: titleSlideIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }

    h5 {
        color: var(--text-secondary, rgba(107, 114, 128, 0.8));
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        line-height: 1.5;
        margin-top: clamp(0.25rem, 0.5vw, 0.375rem);
        opacity: 0.85;
        transition: all 0.3s ease;
        position: relative;
        
        // Animation on load with delay
        animation: subtitleSlideIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s forwards;
        opacity: 0; // Start invisible for animation
    }
    
    // Hover effects for text
    &:hover {
        h3 {
            color: var(--text-primary, rgba(55, 65, 81, 1));
        }
        
        h5 {
            opacity: 1;
            color: var(--text-secondary, rgba(107, 114, 128, 0.9));
    }
    }

    // Responsive Design
    @media (max-width: 768px) {
        width: 100%;
        margin: clamp(0.5rem, 1.5vw, 0.75rem) auto;
        padding: clamp(0.75rem, 2vw, 1rem) clamp(0.5rem, 1.5vw, 0.75rem);
        border-radius: 0.5rem;
        
        h3 {
            margin-bottom: clamp(0.25rem, 0.75vw, 0.375rem);
        }
        
        h5 {
            margin-top: clamp(0.125rem, 0.375vw, 0.25rem);
        }
    }

    @media (max-width: 480px) {
        width: 100%;
        padding: clamp(0.5rem, 1.5vw, 0.75rem) clamp(0.375rem, 1vw, 0.5rem);
        margin: clamp(0.375rem, 1vw, 0.5rem) auto;
        border-radius: 0.375rem;
        
        &:hover {
            transform: none; // Disable hover lift on small screens
        }
    }
}

// Animations
@keyframes titleSlideIn {
    from {
        opacity: 0;
        transform: translateY(15px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes subtitleSlideIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 0.85;
        transform: translateY(0);
    }
}

// Reduced Motion Support
@media (prefers-reduced-motion: reduce) {
    .title {
        transition: none;
        
        &:hover {
            transform: none;
        }
        
        h3, h5 {
            animation: none;
            opacity: 1;
        }
    }
}

// High Performance Mode
.title {
    will-change: transform, background;
    transform: translateZ(0);
    
    h3, h5 {
        will-change: opacity, transform;
    }
}
</style>